
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export const useBillingPortal = (userId: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDowngradeLoading, setIsDowngradeLoading] = useState(false);

  // This function redirects to Stripe billing portal for account management
  const handleManageSubscription = async () => {
    try {
      setIsLoading(true);
      console.log(`Managing subscription for user ${userId}`);
      
      // Get current subscription details to check if there's a Stripe subscription
      const { data: subscription, error: fetchError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (fetchError) {
        console.error("Error fetching subscription:", fetchError);
        toast.error("Failed to access subscription details");
        throw fetchError;
      }
      
      console.log("Found subscription:", subscription);
      
      // First check if we have a stripe_customer_id directly on the subscription
      let stripeCustomerId = subscription?.stripe_customer_id;
      
      // If not, check if it's in the metadata
      if (!stripeCustomerId && subscription?.metadata) {
        const metadata = typeof subscription.metadata === 'string'
          ? JSON.parse(subscription.metadata)
          : subscription.metadata;
          
        stripeCustomerId = metadata?.stripe_customer_id;
      }
      
      // If we don't have a Stripe customer yet, show info message with plan details
      if (!stripeCustomerId) {
        toast.info(`Current plan: ${subscription?.plan || 'No plan'}`);
        return { success: true, subscription };
      }
      
      // Create a Stripe billing portal session
      const { data, error } = await supabase.functions.invoke('create-billing-portal', {
        body: { userId }
      });
      
      if (error) {
        console.error("Error creating billing portal session:", error);
        toast.error("Failed to access subscription management");
        return { success: false, error };
      }
      
      console.log("Billing portal session response:", data);
      
      // Redirect to Stripe billing portal
      if (data?.url) {
        // Use window.location.href to ensure full page redirect
        window.location.href = data.url;
        return { success: true };
      } else {
        console.error("No URL returned from billing portal creation:", data);
        toast.error("Failed to access subscription management");
        return { success: false, error: "No portal URL returned" };
      }
    } catch (error) {
      console.error("Error managing subscription:", error);
      toast.error("Failed to access subscription management");
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  // This function handles subscription downgrades with a custom flow
  const handleDowngradeSubscription = async (
    currentPlan: string, 
    newPlan: string
  ): Promise<{ success: boolean; data?: any; error?: any }> => {
    try {
      setIsDowngradeLoading(true);
      console.log(`Downgrading subscription for user ${userId} from ${currentPlan} to ${newPlan} plan`);
      
      // Call our custom downgrade endpoint with plan names
      const { data, error } = await supabase.functions.invoke('create-downgrade-session', {
        body: { 
          userId,
          newPlan,
          currentPlan
        }
      });
      
      if (error) {
        console.error("Error creating downgrade session:", error);
        toast.error("Failed to initiate downgrade");
        return { success: false, error };
      }
      
      console.log("Downgrade session response:", data);
      
      // Instead of redirecting to a checkout page, we show a success toast
      // since the change has been scheduled for the next billing cycle
      if (data?.success) {
        toast.success(data.message || `Your subscription will be downgraded to ${newPlan} plan.`);
        
        // Return success without redirecting
        return { success: true, data };
      } else {
        console.error("Unexpected response from downgrade session:", data);
        toast.error("Failed to initiate downgrade");
        return { success: false, error: "Unexpected response" };
      }
    } catch (error) {
      console.error("Error downgrading subscription:", error);
      toast.error("Failed to initiate downgrade");
      return { success: false, error };
    } finally {
      setIsDowngradeLoading(false);
    }
  };

  return {
    handleManageSubscription,
    handleDowngradeSubscription,
    isLoading,
    isDowngradeLoading
  };
};
